import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Textarea,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sendTestEmail } from '../../api/auth/mail';

const ContentEditorStep = ({ initialData, onComplete, onBack }) => {
  const FIXED_FROM = 'support@appplatform.co.kr';
  const [subject, setSubject] = useState(initialData.subject || '');
  const [html, setHtml] = useState(initialData.html || '');
  const [testEmails, setTestEmails] = useState('');
  const [isTestSending, setIsTestSending] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link', 'image'],
      [{ color: [] }, { background: [] }],
      ['clean'],
    ],
  };
  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'align',
    'link',
    'image',
    'color',
    'background',
  ];

  const handleTestSend = async () => {
    if (!subject || !html) {
      toast({
        title: '입력 오류',
        description: '제목과 본문을 모두 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const emails = testEmails
      .split(/[,\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));
    if (emails.length === 0) {
      toast({
        title: '이메일 입력 오류',
        description: '테스트 발송할 이메일을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsTestSending(true);
    try {
      await sendTestEmail({ subject, html, to: emails });
      toast({
        title: '테스트 발송 완료',
        description: `${emails.length}명에게 발송되었습니다.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setTestEmails('');
      onClose();
    } catch (error) {
      toast({
        title: '테스트 발송 실패',
        description:
          error.response?.data?.message ||
          '테스트 발송 중 오류가 발생했습니다.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsTestSending(false);
    }
  };

  const handleNext = () => {
    if (!subject || !html) {
      toast({
        title: '입력 오류',
        description: '제목과 본문을 모두 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    onComplete({ subject, from: FIXED_FROM, html });
  };

  return (
    <VStack spacing={6} align="stretch">
      <FormControl isRequired>
        <FormLabel>제목</FormLabel>
        <Input
          placeholder="메일 제목"
          value={subject}
          onChange={e => setSubject(e.target.value)}
        />
      </FormControl>
      <FormControl>
        <FormLabel>발신자 (고정)</FormLabel>
        <Input value={FIXED_FROM} isReadOnly isDisabled />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>본문</FormLabel>
        <Box border="1px solid #e2e8f0" borderRadius="md" minH="400px">
          <ReactQuill
            theme="snow"
            value={html}
            onChange={setHtml}
            modules={modules}
            formats={formats}
            style={{ height: '350px' }}
          />
        </Box>
      </FormControl>
      <HStack spacing={4} justify="space-between" mt={10}>
        <Button onClick={onBack}>이전</Button>
        <HStack spacing={4}>
          <Button colorScheme="teal" variant="outline" onClick={onOpen}>
            테스트 발송
          </Button>
          <Button colorScheme="purple" onClick={handleNext}>
            다음 단계
          </Button>
        </HStack>
      </HStack>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>테스트 메일 발송</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel>
                테스트 수신 이메일 (쉼표 또는 줄바꿈으로 구분)
              </FormLabel>
              <Textarea
                placeholder="test1@example.com, test2@example.com"
                value={testEmails}
                onChange={e => setTestEmails(e.target.value)}
                rows={4}
              />
            </FormControl>
            <HStack spacing={4} mt={6}>
              <Button onClick={onClose}>취소</Button>
              <Button
                colorScheme="teal"
                onClick={handleTestSend}
                isLoading={isTestSending}
              >
                테스트 발송
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ContentEditorStep;
